import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import ar from "./locales/ar.json";
import en from "./locales/en.json";

const dictionaries = { ar, en };
export const supportedLanguages = ["ar", "en"];
export const defaultLanguage = "ar";

const LanguageContext = createContext({ lang: defaultLanguage, t: () => "", raw: dictionaries[defaultLanguage] });

function resolveKey(obj, key) {
  return key.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
}

export function LanguageProvider({ children, forcedLang }) {
  const params = useParams();
  const location = useLocation();
  const lang = useMemo(() => {
    if (forcedLang && supportedLanguages.includes(forcedLang)) return forcedLang;
    if (params.lang && supportedLanguages.includes(params.lang)) return params.lang;
    // Infer from pathname (root '/' -> default)
    const seg = location.pathname.split("/").filter(Boolean)[0];
    if (seg && supportedLanguages.includes(seg)) return seg;
    return defaultLanguage;
  }, [forcedLang, params.lang, location.pathname]);

  const dict = dictionaries[lang] || dictionaries[defaultLanguage];

  const t = useCallback(
    (key, fallback) => {
      const value = resolveKey(dict, key);
      if (value === undefined) return fallback !== undefined ? fallback : key;
      return value;
    },
    [dict]
  );

  const value = useMemo(() => ({ lang, t, raw: dict }), [lang, t, dict]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  return useContext(LanguageContext);
}
